import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import ProductDevelopmentActions from './ProductDevelopmentActions';
import { useStyles } from './GenericTableStyles';

// Max characters before truncating and showing "View More"
const VIEW_MORE_CHAR_LIMIT = 150;

// Format every date in a cell value to the dashboard's "DD-MMM-YYYY" style
// (e.g. "23-JAN-2026"), applied at render time so ALL tabs / columns are
// consistent regardless of how each field was mapped. Matches ISO dates
// (with or without a time part) anywhere in the string — including stacked
// values like "2026-01-23\nStage A" — and leaves everything else untouched.
// Values already in DD-MMM-YYYY don't match the ISO pattern, so no double
// formatting.
const ISO_DATE_RE = /\d{4}-\d{2}-\d{2}(?:T[\d:.]+Z?)?/g;
const formatDatesInValue = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(ISO_DATE_RE, (match) => {
    const d = moment(match);
    return d.isValid() ? d.format('DD-MMM-YYYY').toUpperCase() : match;
  });
};

// Label shown for columns whose "view" action isn't wired up to a backend yet.
// Rendered as blue underlined text that does nothing when clicked -- it reads as
// a link because that is what it will become, but the cursor stays an arrow and
// there is no handler behind it. Driven by the `disabledLink: true` flag on a
// column config (Analytical Report, Supplier Information, Request Form).
const DISABLED_LINK_LABEL = 'Click To View';

// Gap left below the table so the last row isn't flush with the window edge,
// and the smallest height the table is ever squeezed to (below this it scrolls
// too tightly to be usable — at that point the page must scroll instead).
const BOTTOM_GAP = 16;
const MIN_TABLE_HEIGHT = 200;

/**
 * useFitToViewport - Sizes the table so its bottom edge lands at the bottom of
 * the window: maxHeight = viewportHeight - (table's top offset) - gap.
 *
 * The page itself no longer scrolls in the project-tabs view, so the table has
 * to claim exactly the space left under the filters / nav tabs / project row,
 * whose combined height varies by tab and by screen. Measuring at render time
 * handles that without hard-coding an offset (the breakpoint maxHeights in
 * GenericTableStyles remain the pre-measurement fallback).
 */
// The nearest ancestor that is BOTH scrollable and currently overflowing.
//
// "Currently overflowing" is what makes this safe. The wrapper in ProjectTabs
// carrying `overflowX: 'hidden'` also computes `overflowY: auto` -- hidden on one
// axis makes the used value of the other `auto` -- so it looks scrollable, but it
// grows with its content and its scrollHeight never exceeds its clientHeight.
// Requiring real overflow skips it and finds the region that genuinely has a
// bounded height.
const overflowingScrollAncestor = (el) => {
  let node = el?.parentElement || null;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
};

// A correction is applied at most this many times per measurement, so an outer
// region that overflows for some reason the table cannot fix can never drive a
// render loop.
const MAX_FIT_CORRECTIONS = 3;

const useFitToViewport = (ref, deps) => {
  const [maxHeight, setMaxHeight] = useState(null);
  const correctionsRef = useRef(0);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    correctionsRef.current = 0;
    const available = window.innerHeight - el.getBoundingClientRect().top - BOTTOM_GAP;
    setMaxHeight(Math.max(MIN_TABLE_HEIGHT, Math.round(available)));
  }, [ref]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  // Fitting to the window assumes the table is the last thing inside the region
  // holding it. It is not: the container carries a 10px bottom margin, which
  // sits below the table and inside that region, so the region overflowed by
  // exactly 10px and showed a scrollbar of its own beside the table's.
  //
  // Rather than enumerate what sits below -- a margin today, padding or a
  // sibling tomorrow -- this reads how far the region actually overflows and
  // takes that off. It only ever SHRINKS, so it cannot oscillate: one pass
  // removes the overflow, the next finds none and stops. Measured in Chrome:
  // 10px overflow -> one correction -> 0, table still scrolling.
  useLayoutEffect(() => {
    if (maxHeight == null) return;
    if (correctionsRef.current >= MAX_FIT_CORRECTIONS) return;
    const el = ref.current;
    if (!el) return;
    const region = overflowingScrollAncestor(el);
    if (!region) return;
    const overflow = region.scrollHeight - region.clientHeight;
    if (overflow <= 0) return;
    correctionsRef.current += 1;
    setMaxHeight((h) => Math.max(MIN_TABLE_HEIGHT, h - overflow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxHeight, ...deps]);

  return maxHeight;
};

/**
 * ViewMoreCell - Truncates long text to ~4 lines.
 * Shows a blue "View More" link that opens a popup dialog with full text.
 */
const ViewMoreCell = ({ value, columnName }) => {
  const [open, setOpen] = useState(false);
  const text = String(value || '-');

  if (text.length <= VIEW_MORE_CHAR_LIMIT || text === '-') {
    return <span>{text}</span>;
  }

  return (
    <>
      <span>
        {text.slice(0, VIEW_MORE_CHAR_LIMIT)}...
        <span
          onClick={() => setOpen(true)}
          style={{
            color: '#1976d2',
            cursor: 'pointer',
            marginLeft: '4px',
            fontWeight: 500,
            fontSize: '12px',
            whiteSpace: 'nowrap',
          }}
        >
          View More
        </span>
      </span>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { borderRadius: '8px' },
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6" style={{ fontWeight: 600, fontSize: '16px' }}>
            {columnName || 'Details'}
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {/* Render as MUI Typography so the popup uses the theme font (same as
            the table cells). Plain text with fontFamily:'inherit' would pick up
            the body font instead, since the Dialog renders in a portal. */}
        <DialogContent style={{ padding: '20px' }}>
          <Typography
            variant="body2"
            component="div"
            style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
          >
            {text}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * StackedCell - Renders a value made of several parts (joined with "\n") as
 * vertically stacked rows with a thin divider line between each part. Used for
 * "combined" columns whose header contains "/" (e.g. "Reporting Date / Step",
 * "CMO Deviation Status / Category / Type"). Empty parts render as "-".
 */
const StackedCell = ({ value }) => {
  const parts = String(value === undefined || value === null ? '' : value).split('\n');
  return (
    <>
      {parts.map((part, i) => (
        <div
          key={i}
          style={{
            padding: '4px 0',
            borderBottom: i < parts.length - 1 ? '1px solid #e0e0e0' : 'none',
          }}
        >
          {part.trim() === '' ? '-' : part}
        </div>
      ))}
    </>
  );
};

/**
 * GenericTable Component
 *
 * Renders a table based on column configuration and data.
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of row data objects
 * @param {Array} props.columns - Array of column configuration objects
 * @param {string} props.viewType - 'overallView' or 'systemView' (for reference only, backend already filters)
 * @param {Array} props.actions - Optional: Array of action objects for actions column
 * @param {Object} props.featuresAccess - Optional: Feature access object for filtering actions
 * @param {Function} props.onActionClick - Optional: Callback when action is clicked
 * @param {boolean} props.showActionsColumn - Optional: Whether to show actions column (default: false)
 * 
 * Column configuration structure (from API):
 * {
 *   id: 1,                         // Unique identifier for the column
 *   displayName: "Column Name",    // Display name for column header
 *   value: "fieldName"             // Backend field name (camelCase, e.g., "procedureNumber")
 * }
 * 
 * Actions structure:
 * {
 *   label: "Action Label",
 *   title: "Dialog Title",
 *   component: <Component />, // Optional: for dialog actions
 *   isQuickView: true, // Optional: flag for QuickView actions
 *   onClick: (row) => {}, // Optional: custom handler
 *   featureAccess: "featureKey", // Optional: feature access key
 *   quickViewComponent: <Component />, // Optional: QuickView component
 * }
 * 
 * Note: Backend already filters columns based on viewType param, so all columns passed are visible
 * 
 * Data structure:
 * Array of objects where keys match field names from column configuration
 * Example: [{ procedureNumber: "8.2.2", materialDescription: "Ethyl Acetate", ... }]
 */
const GenericTable = ({
  data = [],
  columns = [],
  viewType = "overallView",
  actions = [],
  featuresAccess = {},
  onActionClick = null,
  showActionsColumn = false,
  // Called with (row, column) when a `docLink: true` column's cell is clicked
  // (e.g. "Analytical Report"). Omitted, the column renders empty.
  onDocLinkClick = null,
  // Per-row predicate for `docLink: true` columns: the link renders only for
  // rows it approves, and rows it rejects render an EMPTY cell.
  isDocLinkEnabled = null,
  // In the expanded view the tab strip is pinned and the surrounding region owns
  // the "scroll the filters away" scroll. This prop gives the table a STABLE
  // height (rather than the live fit-to-viewport one) so that region has room to
  // scroll; the table still scrolls internally with its column header pinned, so
  // its horizontal scrollbar stays contained under the table (not the page).
  pageScrollMaxHeight = null,
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);
  const fittedMaxHeight = useFitToViewport(containerRef, [data.length, columns.length]);

  // The TABLE scrolls, not the page — so the column headers can stay put.
  //
  // headerCell is already `position: sticky; top: 0`, but sticky only holds
  // against the nearest SCROLLING ancestor. With no height set here the table
  // grew to its content and never scrolled, so the nearest scrolling ancestor
  // became the wrapper in ProjectTabs carrying `overflowX: 'hidden'` — hidden on
  // one axis makes the used value of the other `auto`, which quietly turns that
  // Box into a scroll container. Its height is its content's height, so there
  // was no distance for the header to hold over and it scrolled away with
  // everything else.
  //
  // Giving this container a bounded height puts the scroll back where the header
  // can hold against it: the rows scroll, the header does not. It also restores a
  // horizontal scrollbar, which that same `overflowX: 'hidden'` had been
  // swallowing — a fourteen-column tab was clipped with no way to reach the
  // right-hand columns at all.
  //
  // pageScrollMaxHeight is what ProjectTabs measures for the pinned layout (the
  // space left under the project row and the tab strip). useFitToViewport is the
  // fallback for any caller that does not measure one, sizing the table so its
  // bottom lands at the bottom of the window. If neither yields a usable number
  // the style stays empty and the table flows as it did before.
  // fittedMaxHeight is preferred because it MEASURES this table's own top and
  // sizes it to the bottom of the window, so everything above it -- filters,
  // search, the project row, the tab strip -- is already accounted for and the
  // expanded region has nothing left to scroll. pageScrollMaxHeight works the
  // height out by subtraction instead and does not know the filter block's
  // height, so the region overflowed by roughly that much and showed a second
  // scrollbar beside the table's own. It stays as the fallback for a render
  // where the measurement has not landed yet.
  const scrollMaxHeight =
    typeof fittedMaxHeight === 'number' && fittedMaxHeight > 0
      ? fittedMaxHeight
      : typeof pageScrollMaxHeight === 'number' && pageScrollMaxHeight > 0
        ? pageScrollMaxHeight
        : null;
  // boxSizing is the whole reason a second scrollbar survived the last fix. The
  // container carries a 1px border top and bottom; under the default content-box
  // maxHeight applies to the content alone, so the element rendered two pixels
  // taller than the height measured for it and the region holding it overflowed
  // by exactly that. Measured in Chrome: content-box leaves 2px of outer
  // overflow, border-box leaves 0, with the table still scrolling either way.
  const containerScrollStyle = scrollMaxHeight
    ? {
      maxHeight: scrollMaxHeight,
      overflowY: 'auto',
      overflowX: 'auto',
      boxSizing: 'border-box',
    }
    : {};

  // Backend already filters columns based on viewType param, so use all columns passed
  // No need to filter here - API returns only the columns for the selected view type
  const visibleColumns = React.useMemo(() => {
    return columns || [];
  }, [columns]);

  // Convert column name to field name (camelCase)
  const getFieldName = (columnName) => {
    return columnName
      .split(' ')
      .map((word, index) => {
        if (index === 0) {
          return word.charAt(0).toLowerCase() + word.slice(1);
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('')
      .replace(/[^a-zA-Z0-9]/g, ''); // Remove special characters
  };

  // Get display name for column
  // Structure: uses name
  // Format UOM as "UoM" to match other dashboards
  // Ensure "ID" doesn't wrap (keep I and D together - they must be on same line)
  const getColumnDisplayName = (column) => {
    let name = column.name || '';
    // Convert UOM to UoM format
    if (name.toUpperCase() === 'UOM' || name === 'U.O.M') {
      name = 'UoM';
    }
    // Replace "ID" to prevent I and D from breaking apart
    // First, replace space before "ID" with non-breaking space (so "ID" moves as unit)
    // Then join I and D with word joiner so they can't separate
    name = name.replace(/\s+ID\b/gi, '\u00A0I\u2060D'); // \u00A0 = non-breaking space, \u2060 = word joiner
    name = name.replace(/\bID\b/gi, 'I\u2060D'); // Handle "ID" at start or standalone (without space before)
    return name;
  };

  // Get field name for column (backend field name)
  // Structure: uses field property
  const getColumnFieldName = (column) => {
    // Use field if provided
    if (column.field) {
      return column.field;
    }
    // Fallback: convert name to camelCase
    return getFieldName(column.name);
  };

  // Get cell class based on column displayName
  const getCellClass = (column) => {
    const displayName = getColumnDisplayName(column);
    const lowerName = displayName.toLowerCase();
    if (lowerName === "uom" || lowerName === "u.o.m") {
      return classes.uomCell;
    }
    return classes.tableCell;
  };

  if (!data || data.length === 0) {
    return (
      <TableContainer 
        component={Paper} 
        className={`${classes.paper} ${classes.tableWrapper}`}
        style={{
          borderTop: '1px solid #C6C6C6',
          borderRight: '1px solid #C6C6C6',
          borderBottom: '1px solid #C6C6C6',
          borderLeft: '1px solid #C6C6C6',
          marginTop: '10px',
          ...containerScrollStyle,
        }}
      >
        <Table className={classes.table} stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => {
                const displayName = getColumnDisplayName(column);
                const lowerName = displayName.toLowerCase();
                const isUOM = lowerName === "uom";
                return (
                  <TableCell
                    key={column.key || column.id || column.name || column.displayName}
                    align="left"
                    className={classes.headerCell}
                    style={isUOM ? { whiteSpace: 'nowrap' } : {}}
                  >
                    {displayName}
                  </TableCell>
                );
              })}
              {showActionsColumn && actions && actions.length > 0 && (
                <TableCell align="center" className={classes.headerCell}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={visibleColumns.length + (showActionsColumn && actions && actions.length > 0 ? 1 : 0)} align="center" className={classes.tableCell}>
                No data available
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer
      ref={containerRef}
      component={Paper}
      className={`${classes.paper} ${classes.tableWrapper}`}
      style={{
        borderTop: '1px solid #C6C6C6',
        borderRight: '1px solid #C6C6C6',
        borderBottom: '1px solid #C6C6C6',
        borderLeft: '1px solid #C6C6C6',
        marginTop: '10px',
        ...containerScrollStyle,
      }}
    >
      <Table className={classes.table} stickyHeader>
        <TableHead>
          <TableRow>
            {visibleColumns.map((column) => {
              const displayName = getColumnDisplayName(column);
              const lowerName = displayName.toLowerCase();
              const isUOM = lowerName === "uom";
              const isCentered = column.centered === true;
              return (
                <TableCell
                  key={column.key || column.id || column.name || column.displayName}
                  align={isCentered ? "center" : "left"}
                  className={classes.headerCell}
                  style={isUOM ? { whiteSpace: 'nowrap' } : {}}
                >
                  {displayName}
                </TableCell>
              );
            })}
            {showActionsColumn && actions && actions.length > 0 && (
              <TableCell align="center" className={classes.headerCell}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index} className={classes.tableRow}>
              {visibleColumns.map((column) => {
                const fieldName = getColumnFieldName(column);
                // No value → empty cell (not "-"/"NA").
                const rawCellValue =
                  row[fieldName] !== undefined && row[fieldName] !== null
                    ? row[fieldName]
                    : '';
                // Columns flagged `rawValue: true` bypass the auto date-format
                // (e.g. Deviation Assessment / Final Deviation Report timestamps
                // are shown exactly as the backend sends them).
                const cellValue = column.rawValue === true
                  ? rawCellValue
                  : formatDatesInValue(rawCellValue);

                // Check if value is PDF (ends with .pdf) or contains PDF link
                const cellValueStr = String(cellValue);
                const isPdf = cellValueStr.trim().toLowerCase().endsWith('.pdf') ||
                             cellValueStr.trim().toLowerCase().includes('.pdf');
                const isCentered = column.centered === true;
                const isLongText = cellValueStr.length > VIEW_MORE_CHAR_LIMIT;
                const isDisabledLink = column.disabledLink === true;
                // docLink columns are clickable when a handler is provided;
                // otherwise they fall back to the same disabled look.
                const isDocLink = column.docLink === true;
                const docLinkHandlerAvailable =
                  isDocLink &&
                  typeof onDocLinkClick === 'function' &&
                  (typeof isDocLinkEnabled !== 'function' || isDocLinkEnabled(row, column));
                // Combined columns whose header contains "/" (e.g. "Reporting
                // Date / Step") embed "\n" separators between their parts —
                // rendered as stacked rows with a divider line between each.
                const isStacked = column.stacked === true;
                const displayName = getColumnDisplayName(column);

                return (
                  <TableCell
                    key={column.key || column.id || column.name || column.displayName}
                    align={isCentered ? "center" : "left"}
                    className={getCellClass(column)}
                  >
                    {isDocLink && !docLinkHandlerAvailable ? (
                      // FSD: "Tests with no Analytical Report show no link
                      // under the Analytical Report column".
                      null
                    ) : isDisabledLink ? (
                      <span
                        aria-disabled="true"
                        style={{
                          color: '#1976d2',
                          textDecoration: 'underline',
                          cursor: 'default',
                        }}
                      >
                        {DISABLED_LINK_LABEL}
                      </span>
                    ) : docLinkHandlerAvailable ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => onDocLinkClick(row, column)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') onDocLinkClick(row, column);
                        }}
                        style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {DISABLED_LINK_LABEL}
                      </span>
                    ) : isStacked ? (
                      <StackedCell value={cellValue} />
                    ) : isPdf ? (
                      <span style={{ color: '#1976d2', cursor: 'pointer' }}>
                        {cellValue}
                      </span>
                    ) : isLongText ? (
                      <ViewMoreCell value={cellValue} columnName={displayName} />
                    ) : (
                      cellValue
                    )}
                  </TableCell>
                );
              })}
              {showActionsColumn && actions && actions.length > 0 && (
                <TableCell align="center" className={classes.tableCell}>
                  <ProductDevelopmentActions
                    row={row}
                    actions={actions}
                    featuresAccess={featuresAccess}
                    onActionClick={onActionClick}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GenericTable;

